import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useLocation, useSearchParams } from "react-router-dom";
import { parseSlugPath, isListingSlug } from "../utils/slugutils";
import { useSearch } from "../context/searchcontext";
import SearchBar from "./searchbar";
import { getListingLandingPageForTenant } from '../apis/services/pgApi';
import { performLocationSearch } from '../utils/searchService';
 
const SORT_OPTIONS = [
  { value: "",           label: "Sort by" },
  { value: "price-asc", label: "Price: Low-High" },
  { value: "price-desc",label: "Price: High-Low" },
  { value: "popular",   label: "Most Popular" },
  { value: "newest",    label: "Newest First" },
];
 
// ─── Sort Dropdown ────────────────────────────────────────────────────────────
const SortDropdown = ({ value, onChange }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
 
  useEffect(() => {
    const h = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);
 
  const selected = SORT_OPTIONS.find(o => o.value === value && o.value !== '') || null;
  const hasValue = !!selected;
 
  return (
    <div className={`sort-dropdown ${open ? 'open' : ''}`} ref={ref}>
      <button className="sort-dropdown-trigger" onClick={() => setOpen(o => !o)}>
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
          <line x1="3" y1="6" x2="21" y2="6" />
          <line x1="3" y1="12" x2="15" y2="12" />
          <line x1="3" y1="18" x2="9" y2="18" />
        </svg>
        <span>{selected ? selected.label : 'Sort by'}</span>
        <svg width="13" height="13" viewBox="0 0 14 14" fill="none">
          <path d="M3 5L7 9L11 5" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </button>
 
      {open && (
        <div className="sort-dropdown-menu">
          <p className="sort-dropdown-heading">Sort results</p>
          {SORT_OPTIONS.slice(1).map(opt => (
            <button
              key={opt.value}
              className={`sort-dropdown-item ${value === opt.value ? 'selected' : ''}`}
              onClick={() => { onChange(opt.value); setOpen(false); }}
            >
              {hasValue && (
                <span className="sort-check">
                  {value === opt.value && (
                    <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                      <path d="M2 6L5 9L10 3" stroke="#155DFC" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  )}
                </span>
              )}
              {opt.label}
            </button>
          ))}
          {hasValue && (
            <>
              <div className="sort-dropdown-divider" />
              <button className="sort-dropdown-clear" onClick={() => { onChange(''); setOpen(false); }}>
                × Clear sort
              </button>
            </>
          )}
        </div>
      )}
    </div>
  );
};
 
// ─── PG Card ──────────────────────────────────────────────────────────────────
const PGCard = ({ pg, locationName }) => {
  const navigate = useNavigate();
  const [saved, setSaved] = useState(false);
 
  const createSafeSlug = (name) => {
    if (!name) return '';
    return name
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '');
  };
 
  const genderClass =
    pg.PGType?.includes("Boys")
      ? "gender-boys"
      : pg.PGType?.includes("Girls")
        ? "gender-girls"
        : "gender-coed";
 
  const roomTypes = [pg.RoomType1, pg.RoomType2, pg.RoomType3].filter(r => r && r.trim() !== '');
  const amenities = [pg.Category1, pg.Category2, pg.Category3].filter(a => a && a.trim() !== '');
 
  const getImageUrl = () => {
    if (pg.PGPhoto && pg.PGPhoto !== 'null' && pg.PGPhoto !== '') return pg.PGPhoto;
    return null;
  };
 
  const rentAmount = parseFloat(pg.PGRent) || 0;
 
  return (
    <div
      className="listing-card"
      onClick={() => {
        const slug = createSafeSlug(pg.PGName);
        navigate(`/pg/${slug}-${pg.PGID}`, { state: { PGNAME: pg.PGName, PGID: pg.PGID } });
      }}
    >
      <div className="listing-card-image">
        <img src={getImageUrl()} alt={pg.PGName} loading="lazy" />
        <span className={`card-gender-badge ${genderClass}`}>{pg.PGType}</span>
        <button
          className={`card-save-btn ${saved ? "saved" : ""}`}
          onClick={(e) => { e.stopPropagation(); setSaved(s => !s); }}
          aria-label="Save PG"
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill={saved ? "#155DFC" : "none"} stroke={saved ? "#155DFC" : "#fff"} strokeWidth="2">
            <path d="M19 21l-7-5-7 5V5a2 2 0 012-2h10a2 2 0 012 2z" />
          </svg>
        </button>
      </div>
 
      <div className="listing-card-body">
        <div className="card-name-row">
          <h3 className="card-name">{pg.PGName}</h3>
        </div>
 
        <p className="card-location">
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0118 0z" />
            <circle cx="12" cy="10" r="3" />
          </svg>
          {pg.PGAddress || pg.PGLocality || locationName}, {pg.PGCity || 'Delhi'}
        </p>
 
        {roomTypes.length > 0 && (
          <div className="card-pills">
            {roomTypes.slice(0, 3).map((room, idx) => <span key={idx} className="card-pill">{room}</span>)}
            {roomTypes.length > 3 && <span className="card-pill card-pill-more">+{roomTypes.length - 3} more</span>}
          </div>
        )}
 
        {amenities.length > 0 && (
          <div className="card-pills card-amenities">
            {amenities.slice(0, 3).map((amenity, idx) => <span key={idx} className="card-pill-amenity">{amenity}</span>)}
            {amenities.length > 3 && <span className="card-pill card-pill-more">+{amenities.length - 3} more</span>}
          </div>
        )}
 
        <div className="card-divider" />
 
        <div className="card-footer">
          <div className="card-price">
            {pg.PGRent ? (
              <>
                <span className="card-price-amount">₹{rentAmount.toLocaleString("en-IN")}</span>
                <span className="card-price-unit">/month</span>
              </>
            ) : (
              <span className="card-price-contact">For rent contact PG</span>
            )}
          </div>
          <button
            className="card-view-btn"
            onClick={(e) => {
              e.stopPropagation();
              const slug = createSafeSlug(pg.PGName);
              navigate(`/pg/${slug}-${pg.PGID}`, { state: { PGNAME: pg.PGName, PGID: pg.PGID } });
            }}
          >
            View Details
          </button>
        </div>
      </div>
    </div>
  );
};
 
// ─── Listings Page ────────────────────────────────────────────────────────────
const Listings = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
 
  const {
    location: contextLocation, setLocation,
    gender:   contextGender,   setGender,
    price:    contextPrice,    setPrice,
    sharing:  contextSharing,  setSharing,
    searchResults, setSearchResults,
    nearbyPincodes, setNearbyPincodes,
    searchCoordinates, setSearchCoordinates,
    isSearching, setIsSearching,
  } = useSearch();
 
  const [sortValue, setSortValue] = useState("");
  const [isStuck, setIsStuck] = useState(false);
  const [filteredPGs, setFilteredPGs] = useState([]);
  const [loading, setLoading] = useState(true);
  const sentinelRef = useRef(null);
  const isInitialMount = useRef(true);
  const [isSearchLoading, setIsSearchLoading] = useState(false);
  
  // Track current URL location to detect changes
  const currentUrlLocationRef = useRef(null);
  // Track the location we're currently searching for
  const searchingLocationRef = useRef(null);
  
  // NEW: Store the stable display location (only updates when search is performed)
  const [stableDisplayLocation, setStableDisplayLocation] = useState('');
 
  // ── Unified URL parsing — supports both slug and legacy query-param formats ──
  const urlFilters = isListingSlug(location.pathname)
    ? parseSlugPath(location.pathname)
    : parseSlugPath(location.pathname, searchParams);
 
  const urlLocation = urlFilters.location;
  const urlGender   = urlFilters.gender;
  const urlPrice    = urlFilters.price;
  const urlSharing  = urlFilters.sharing;
  const activeLocation = urlLocation || contextLocation;
 
  // ── Get display location (NOW uses stable display location) ──
  const getDisplayLocation = () => {
    // If we have a stable display location, use it
    if (stableDisplayLocation) {
      return stableDisplayLocation;
    }
    
    // Fallback: use URL location or context location
    return urlLocation || contextLocation || "Delhi NCR";
  };

  const displayLocation = getDisplayLocation();
  const showLoading = isSearchLoading || (loading && !searchResults?.length);
 
  // ── Trigger search from URL params ────────────────────────────────────────
  const triggerSearchFromParams = async () => {
    const locationParam = urlLocation;
    const genderParam   = urlGender;
    const priceParam    = urlPrice;
    const sharingParam  = urlSharing;

    if (!locationParam) {
      console.log('❌ No location parameter found');
      return false;
    }

    // Store the location we're searching for
    searchingLocationRef.current = locationParam;
    
    // NEW: Update stable display location immediately
    setStableDisplayLocation(locationParam);
    
    console.log('🔄 Triggering search from URL params for:', locationParam);
    setIsSearchLoading(true);
    setLoading(true);
    setIsSearching(true);

    const result = await performLocationSearch(
      locationParam,
      genderParam || contextGender,
      priceParam  || contextPrice,
      sharingParam || contextSharing
    );

    if (result.success) {
      console.log('✅ Search successful, found:', result.results.length, 'PGs');
      console.log('📍 Nearby pincodes:', result.nearbyPincodes);

      // Update context
      if (genderParam && genderParam !== 'Any Gender') setGender(genderParam);
      if (priceParam && priceParam !== 'Any Price') setPrice(priceParam);
      if (sharingParam && sharingParam !== 'Any Type') setSharing(sharingParam);
      
      setSearchResults(result.results);
      setNearbyPincodes(result.nearbyPincodes);
      setSearchCoordinates(result.coordinates);
      setLocation(locationParam);
      
      // Apply filters to results
      const filtered = applyFilters(result.results, true, {
        gender: genderParam || contextGender,
        price: priceParam || contextPrice,
        sharing: sharingParam || contextSharing,
      });
      setFilteredPGs(filtered);
      
      // NEW: Ensure stable display location is set
      setStableDisplayLocation(locationParam);
    } else {
      console.error('❌ Search failed:', result.error);
      setSearchResults([]);
      setNearbyPincodes([]);
      setSearchCoordinates(null);
      setFilteredPGs([]);
    }

    // Clear the searching location reference
    searchingLocationRef.current = null;
    setIsSearchLoading(false);
    setLoading(false);
    setIsSearching(false);
    return result.success;
  };
  
  // Helper function to apply filters (same as before)
  const applyFilters = (pgs, skipLocationFilter = false, overrides = {}) => {
    if (!pgs || pgs.length === 0) return [];

    const genderFilter  = overrides.gender  !== undefined ? overrides.gender  : contextGender;
    const priceFilter   = overrides.price   !== undefined ? overrides.price   : contextPrice;
    const sharingFilter = overrides.sharing !== undefined ? overrides.sharing : contextSharing;

    let filtered = [...pgs];

    if (!skipLocationFilter) {
      const locationToFilter = (searchParams.get('location') || contextLocation || '').trim();
      if (locationToFilter) {
        const searchLower = locationToFilter.toLowerCase();
        const isDelhiCity = searchLower === 'delhi' || searchLower === 'new delhi' || searchLower === 'delhi ncr';
        if (!isDelhiCity) {
          filtered = filtered.filter(pg => {
            const pgLocality = (pg.PGLocality || pg.pglocality || '').toLowerCase();
            const pgAddress  = (pg.PGAddress  || '').toLowerCase();
            return pgLocality.includes(searchLower) || pgAddress.includes(searchLower);
          });
        }
      }
    }

    if (genderFilter && genderFilter !== 'Any Gender') {
      filtered = filtered.filter(pg => pg.PGType === genderFilter);
    }

    if (priceFilter && priceFilter !== 'Any Price') {
      filtered = filtered.filter(pg => {
        const rent = parseFloat(pg.PGRent);
        if (!rent) return false;

        const amount = parseInt(priceFilter.replace(/[^\d]/g, ''), 10);

        if (priceFilter.startsWith('Under')) return rent < amount;
        if (priceFilter.startsWith('Above')) return rent > amount;

        const rangeMatch = priceFilter.match(/₹([\d,]+)[–-]₹([\d,]+)/);
        if (rangeMatch) {
          const lo = parseInt(rangeMatch[1].replace(/,/g, ''), 10);
          const hi = parseInt(rangeMatch[2].replace(/,/g, ''), 10);
          return rent >= lo && rent <= hi;
        }

        return true;
      });
    }

    if (sharingFilter && sharingFilter !== 'Any Type') {
      filtered = filtered.filter(pg => {
        const roomTypesList = [pg.RoomType1, pg.RoomType2, pg.RoomType3];
        return roomTypesList.some(rt => rt?.toLowerCase() === sharingFilter.toLowerCase());
      });
    }

    console.log('📊 After filters:', filtered.length, 'PGs');
    return filtered;
  };
 
  // ── Fetch PGs for a category click (no location — filter only) ───────────
  // Sends the correct payload: { SearchText: null, PGRentRange: null, PGType, PinCode: null }
  const fetchCategoryPGs = async (gender = '', price = '', sharing = '') => {
    try {
      setLoading(true);

      const pgType = gender && gender !== 'Any Gender' ? gender : null;
      const rentRange = price && price !== 'Any Price' ? price : null;

      const payload = {
        SearchText: null,
        PGRentRange: rentRange,
        PGType: pgType,
        PinCode: null,
      };

      console.log('📡 Category fetch — payload:', JSON.stringify(payload));
      const response = await getListingLandingPageForTenant(payload);

      let pgData = [];
      if (response && Array.isArray(response)) pgData = response;
      else if (response?.data) pgData = response.data;
      else if (response?.recordsets?.[0]) pgData = response.recordsets[0];

      // Apply sharing filter client-side (not in API payload)
      const filtered = applyFilters(pgData, true, { gender, price, sharing });
      setFilteredPGs(filtered);
      setSearchResults(pgData);
      
      // NEW: Clear stable display location when browsing without location
      setStableDisplayLocation('');
    } catch (error) {
      console.error('Error fetching category PGs:', error);
      setFilteredPGs([]);
    } finally {
      setLoading(false);
    }
  };

  // ── Fetch PGs - true fallback (no location, no filters) ──────────────────
  const fetchAllPGs = async () => {
    try {
      setLoading(true);

      const payload = {
        SearchText: null,
        PGRentRange: null,
        PGType: null,
        PinCode: null,
      };

      console.log('📡 Default fetch — payload:', JSON.stringify(payload));
      const response = await getListingLandingPageForTenant(payload);

      let pgData = [];
      if (response && Array.isArray(response)) pgData = response;
      else if (response?.data) pgData = response.data;
      else if (response?.recordsets?.[0]) pgData = response.recordsets[0];

      const filtered = applyFilters(pgData, true);
      setFilteredPGs(filtered);
      setSearchResults(pgData);
      
      // NEW: Clear stable display location when browsing without location
      setStableDisplayLocation('');
    } catch (error) {
      console.error('Error fetching PGs:', error);
      setFilteredPGs([]);
    } finally {
      setLoading(false);
    }
  };

  // ── Decide which no-location fetch to run ─────────────────────────────────
  const fetchWithoutLocation = (gender = contextGender, price = contextPrice, sharing = contextSharing) => {
    const hasFilter = (gender && gender !== 'Any Gender')
      || (price && price !== 'Any Price')
      || (sharing && sharing !== 'Any Type');
    if (hasFilter) {
      fetchCategoryPGs(gender, price, sharing);
    } else {
      fetchAllPGs();
    }
  };

  // ── Watch URL changes and trigger search ───────────────────────────────────
  useEffect(() => {
    const currentLocation = urlLocation;

    if (currentLocation && currentLocation !== currentUrlLocationRef.current) {
      console.log(`📍 Location changed from "${currentUrlLocationRef.current}" to "${currentLocation}"`);
      currentUrlLocationRef.current = currentLocation;
      triggerSearchFromParams();
    } else if (!currentLocation && !currentUrlLocationRef.current) {
      // No location in URL — category browse or plain /listings
      if (!searchResults?.length) {
        fetchWithoutLocation();
      }
    }
  }, [urlLocation, location.pathname, location.search]);

  // ── Initial mount - load data if no location ──────────────────────────────
  useEffect(() => {
    if (isInitialMount.current) {
      if (!urlLocation) {
        console.log('🏠 Initial mount — no location, checking for filters');
        fetchWithoutLocation();
      }
      isInitialMount.current = false;
    }
  }, []);

  // ── Re-apply filters when context filters change ──────────────────────────
  useEffect(() => {
    if (searchResults && searchResults.length > 0) {
      console.log('🔄 Re-applying filters due to filter change');
      setFilteredPGs(applyFilters(searchResults, true));
    } else if (!searchResults?.length && !urlLocation) {
      fetchWithoutLocation(contextGender, contextPrice, contextSharing);
    }
  }, [contextGender, contextPrice, contextSharing]);
 
  // Apply sorting
  const sortedPGs = [...filteredPGs].sort((a, b) => {
    if (sortValue === 'price-asc') return (parseFloat(a.PGRent) || 0) - (parseFloat(b.PGRent) || 0);
    if (sortValue === 'price-desc') return (parseFloat(b.PGRent) || 0) - (parseFloat(a.PGRent) || 0);
    return 0;
  });
 
  // Sticky shadow
  useEffect(() => {
    const sentinel = sentinelRef.current;
    if (!sentinel) return;
    const observer = new IntersectionObserver(([e]) => setIsStuck(!e.isIntersecting), { threshold: 0 });
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, []);
 
  // Show loading until search is complete
  if (showLoading) {
    return (
      <main className="listings-page page">
        <div className="container">
          <div className="loading-spinner">
            {isSearchLoading ? `Searching for PGs in ${displayLocation}...` : `Loading PGs in ${displayLocation}...`}
          </div>
        </div>
      </main>
    );
  }
 
  return (
    <main className="listings-page page">
      <div ref={sentinelRef} className="sticky-sentinel" />
 
      <div className="listings-results container">
        <div className="listings-results-bar">
          <div className="listings-results-left">
            {sortedPGs.length !== 0 && (
              <h1 className="listings-results-heading">
                <strong>{sortedPGs.length} PGs available</strong> in {displayLocation}
              </h1>
            )}
          </div>
          <SortDropdown value={sortValue} onChange={setSortValue} />
        </div>
 
        {sortedPGs.length === 0 && !showLoading ? (
          <div className="no-results" style={{ textAlign: 'center', padding: '60px 20px' }}>
            <p>No PGs found matching your criteria in {displayLocation}.</p>
            <p>Try adjusting your filters or search in a different area.</p>
          </div>
        ) : (
          <div className="listings-grid">
            {sortedPGs.map((pg, idx) => (
              <PGCard key={pg.PGID || idx} pg={pg} locationName={displayLocation} />
            ))}
          </div>
        )}
      </div>
    </main>
  );
};
 
export default Listings;